import OssTurniLegend from "../../components/oss/OssTurniLegend";
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Calendar, ChevronLeft, ChevronRight, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_TURNI_CONFIG = {
  schema_orario: "standard",
  max_notti_consecutive: 3,
  riposo_dopo_notti: 1,
  orari: {
    mattina: { inizio: "07:00", fine: "14:00" },
    pomeriggio: { inizio: "14:00", fine: "21:00" },
    notte: { inizio: "21:00", fine: "07:00" },
    lunga: { inizio: "07:00", fine: "21:00" },
  },
};

function mergeTurniConfig(config) {
  return {
    ...DEFAULT_TURNI_CONFIG,
    ...(config || {}),
    orari: { ...DEFAULT_TURNI_CONFIG.orari, ...(config?.orari || {}) },
  };
}

function formatApiDetail(detail, fallback) {
  if (detail == null || detail === "") return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") return item.msg || item.message || "";
        return "";
      })
      .filter(Boolean);
    return parts.join(". ") || fallback;
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || fallback;
  }
  return fallback;
}

function asPositiveInt(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getMonthRange(firstDay) {
  const from = firstDay;
  const d = new Date(`${firstDay}T12:00:00`);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return { from, to: formatDateLocal(d) };
}

function getDayLetterIt(date) {
  return ["D", "L", "M", "M", "G", "V", "S"][date.getDay()];
}

function cellInfo(dayTurni, ferieDay) {
  if (ferieDay) {
    return { code: "F", bg: "bg-amber-200", text: "text-amber-900", label: "Ferie" };
  }
  if (!dayTurni.length) return null;
  if (dayTurni.some((t) => t.tipo_giorno === "smontante" || t.turno === "smontante")) {
    return { code: "S", bg: "bg-indigo-200", text: "text-indigo-900", label: "Smontante" };
  }
  if (dayTurni.some((t) => t.tipo_giorno === "riposo" || t.turno === "riposo")) {
    return { code: "R", bg: "bg-violet-200", text: "text-violet-900", label: "Riposo" };
  }
  const hasM = dayTurni.some((t) => t.turno === "mattina");
  const hasP = dayTurni.some((t) => t.turno === "pomeriggio");
  const hasN = dayTurni.some((t) => t.turno === "notte");
  if (hasM && hasP) return { code: "M-P", bg: "bg-emerald-300", text: "text-emerald-900", label: "Lunga" };
  if (hasM) return { code: "M", bg: "bg-sky-200", text: "text-sky-900", label: "Mattina" };
  if (hasP) return { code: "P", bg: "bg-rose-200", text: "text-rose-900", label: "Pomeriggio" };
  if (hasN) return { code: "N", bg: "bg-slate-300", text: "text-slate-800", label: "Notte" };
  return null;
}

const TURNI_OPTIONS = [
  { value: "mattina", label: "Mattina (M)" },
  { value: "pomeriggio", label: "Pomeriggio (P)" },
  { value: "notte", label: "Notte (N)" },
  { value: "lunga", label: "Lunga (M-P)" },
];

export default function OssTurniGestione() {
  const { token, activeOss } = useOssAuth();
  const [staff, setStaff] = useState([]);
  const [turni, setTurni] = useState([]);
  const [ferie, setFerie] = useState([]);
  const [turniConfig, setTurniConfig] = useState(DEFAULT_TURNI_CONFIG);
  const [loading, setLoading] = useState(false);
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return `${d.toISOString().slice(0, 7)}-01`;
  });
  const [editCell, setEditCell] = useState(null); // { ossId, dateStr, nome }
  const [saving, setSaving] = useState(false);
  const [showGenera, setShowGenera] = useState(false);
  const [generaMonth, setGeneraMonth] = useState("");
  const [generaOssIds, setGeneraOssIds] = useState([]);
  const [generaSostituisci, setGeneraSostituisci] = useState(true);
  const [generaMattina, setGeneraMattina] = useState(1);
  const [generaPomeriggio, setGeneraPomeriggio] = useState(1);
  const [generaNotte, setGeneraNotte] = useState(1);
  const [generating, setGenerating] = useState(false);

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : null),
    [token]
  );

  const ossStaff = useMemo(
    () =>
      staff.filter(
        (s) =>
          s.id &&
          ((s.figura || "oss") === "oss" || s.has_oss_area === true)
      ),
    [staff]
  );

  const load = useCallback(async () => {
    if (!headers || !monthStart) return;
    const { from, to } = getMonthRange(monthStart);
    setLoading(true);
    try {
      const [staffRes, turniRes, ferieRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/oss/me/staff`, { headers }),
        axios.get(`${API_URL}/api/oss/me/turni/gestione`, {
          params: { from_date: from, to_date: to },
          headers,
        }),
        axios.get(`${API_URL}/api/oss/me/ferie/gestione`, {
          params: { from_date: from, to_date: to },
          headers,
        }),
        axios.get(`${API_URL}/api/oss/me/turni-config`, { headers }),
      ]);
      setStaff(staffRes.data.oss || []);
      setTurni(turniRes.data.turni || []);
      setFerie(ferieRes.data.ferie || []);
      setTurniConfig(mergeTurniConfig(configRes.data.config));
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore caricamento turni"));
      setStaff([]);
      setTurni([]);
      setFerie([]);
    } finally {
      setLoading(false);
    }
  }, [headers, monthStart]);

  useEffect(() => {
    load();
  }, [load]);

  const turniByOssDate = useMemo(() => {
    const map = {};
    turni.forEach((t) => {
      const key = `${t.oss_id}|${t.data}`;
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [turni]);

  const ferieByOssDate = useMemo(() => {
    const map = {};
    ferie.forEach((f) => {
      const from = f.from_date;
      const to = f.to_date;
      if (!from || !to || !f.oss_id) return;
      const d = new Date(`${from}T12:00:00`);
      const end = new Date(`${to}T12:00:00`);
      while (d <= end) {
        map[`${f.oss_id}|${formatDateLocal(d)}`] = true;
        d.setDate(d.getDate() + 1);
      }
    });
    return map;
  }, [ferie]);

  const first = new Date(`${monthStart}T12:00:00`);
  const year = first.getFullYear();
  const month = first.getMonth();
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
  const daysInMonth = [];
  for (let i = 1; i <= lastDayOfMonth; i++) {
    const d = new Date(year, month, i);
    daysInMonth.push({
      dateStr: formatDateLocal(d),
      dayNum: i,
      dayLetter: getDayLetterIt(d),
      isSunday: d.getDay() === 0,
      isSaturday: d.getDay() === 6,
    });
  }

  const openEdit = (ossRow, dateStr) => {
    const key = `${ossRow.id}|${dateStr}`;
    if (ferieByOssDate[key]) {
      toast.error("L'operatore è in ferie in questa data");
      return;
    }
    setEditCell({
      ossId: ossRow.id,
      dateStr,
      nome: `${ossRow.cognome || ""} ${ossRow.nome || ""}`.trim(),
      existing: turniByOssDate[key] || [],
    });
  };

  const assignTurno = async (turnoType) => {
    if (!editCell || !headers) return;
    setSaving(true);
    try {
      // Rimuovi turni esistenti del giorno
      for (const t of editCell.existing) {
        if (t.id) {
          await axios.delete(`${API_URL}/api/oss/me/turni/gestione/${t.id}`, { headers });
        }
      }
      const types = turnoType === "lunga" ? ["mattina", "pomeriggio"] : [turnoType];
      for (const turno of types) {
        await axios.post(
          `${API_URL}/api/oss/me/turni/gestione`,
          { oss_id: editCell.ossId, data: editCell.dateStr, turno, tipo_giorno: "turno" },
          { headers }
        );
      }
      toast.success("Turno aggiornato");
      setEditCell(null);
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore assegnazione turno"));
    } finally {
      setSaving(false);
    }
  };

  const clearTurni = async () => {
    if (!editCell || !headers) return;
    setSaving(true);
    try {
      for (const t of editCell.existing) {
        if (t.id) {
          await axios.delete(`${API_URL}/api/oss/me/turni/gestione/${t.id}`, { headers });
        }
      }
      toast.success("Turni rimossi");
      setEditCell(null);
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore rimozione"));
    } finally {
      setSaving(false);
    }
  };

  const openGenera = () => {
    setGeneraMonth(monthStart.slice(0, 7));
    setGeneraOssIds(ossStaff.map((s) => s.id).filter(Boolean));
    setShowGenera(true);
  };

  const toggleGeneraOss = (id) => {
    setGeneraOssIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const runGenera = async () => {
    const monthParam = String(generaMonth || "").slice(0, 7);
    const ossIds = generaOssIds.filter(Boolean);
    if (!headers || !ossIds.length) {
      toast.error("Seleziona almeno un OSS");
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(monthParam)) {
      toast.error("Seleziona un mese valido");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/oss/me/turni/gestione/genera`,
        {
          month: monthParam,
          oss_ids: ossIds,
          sostituisci: Boolean(generaSostituisci),
          modalita: "copertura",
          turni_mattina_per_giorno: asPositiveInt(generaMattina, 1),
          turni_pomeriggio_per_giorno: asPositiveInt(generaPomeriggio, 1),
          turni_notte_per_giorno: asPositiveInt(generaNotte, 1),
          max_notti_consecutive: asPositiveInt(turniConfig.max_notti_consecutive, 3),
          riposo_dopo_notti: asPositiveInt(turniConfig.riposo_dopo_notti, 1),
        },
        { headers }
      );
      toast.success(data.message || `Generati ${data.generated || 0} turni`);
      if (data.warnings?.length) {
        data.warnings.slice(0, 3).forEach((w) => {
          toast.warning(typeof w === "string" ? w : w?.msg || w?.message || "Avviso generazione");
        });
      }
      setShowGenera(false);
      setMonthStart(`${monthParam}-01`);
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Errore generazione turni"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <OssSectionPageHeader
        sectionKey="turni"
        title="Gestione turni"
        subtitle={`Come OSS Responsabile puoi vedere e modificare i turni di tutti · ${activeOss?.nome || ""}`}
      />

      <Card className="card-base oss-surface bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-emerald-700">
              <Calendar className="w-5 h-5 shrink-0" />
              Calendario turni struttura
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={openGenera}
                className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px] touch-manipulation"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Genera turni
              </Button>
              <Button variant="outline" size="icon" className="h-11 w-11 touch-manipulation" onClick={() => {
                const d = new Date(`${monthStart}T12:00:00`);
                d.setMonth(d.getMonth() - 1);
                setMonthStart(`${d.toISOString().slice(0, 7)}-01`);
              }}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center uppercase">
                {first.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
              </span>
              <Button variant="outline" size="icon" className="h-11 w-11 touch-manipulation" onClick={() => {
                const d = new Date(`${monthStart}T12:00:00`);
                d.setMonth(d.getMonth() + 1);
                setMonthStart(`${d.toISOString().slice(0, 7)}-01`);
              }}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Clicca su una cella per assegnare o rimuovere un turno.
          </p>
        </CardHeader>
        <CardContent className="p-3 sm:p-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : ossStaff.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">Nessun OSS in struttura</p>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="border-collapse text-xs" style={{ tableLayout: "fixed" }}>
                <thead>
                  <tr>
                    <th
                      rowSpan={2}
                      className="sticky left-0 z-20 bg-slate-100 border border-slate-300 px-2 py-1 text-left font-semibold text-slate-700"
                      style={{ width: 120, minWidth: 120 }}
                    >
                      OSS
                    </th>
                    {daysInMonth.map((d) => (
                      <th
                        key={`dl-${d.dateStr}`}
                        className={`border border-slate-300 px-0 py-1 text-center font-semibold ${
                          d.isSunday ? "bg-rose-100 text-rose-700" : d.isSaturday ? "bg-slate-50" : "bg-slate-100"
                        }`}
                        style={{ width: 44, minWidth: 44 }}
                      >
                        {d.dayLetter}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {daysInMonth.map((d) => (
                      <th
                        key={`dn-${d.dateStr}`}
                        className={`border border-slate-300 px-0 py-1 text-center font-semibold ${
                          d.isSunday ? "bg-rose-100 text-rose-700" : d.isSaturday ? "bg-slate-50" : "bg-slate-100"
                        }`}
                        style={{ width: 44, minWidth: 44 }}
                      >
                        {d.dayNum}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ossStaff.map((row) => (
                    <tr key={row.id}>
                      <td
                        className="sticky left-0 z-10 border border-slate-300 px-2 py-1 font-semibold text-slate-800 bg-white"
                        style={{ width: 120, minWidth: 120 }}
                      >
                        <div className="truncate uppercase text-[11px] leading-tight">{row.cognome || "—"}</div>
                        <div className="truncate text-[10px] font-normal text-slate-500 leading-tight">{row.nome || ""}</div>
                      </td>
                      {daysInMonth.map((d) => {
                        const key = `${row.id}|${d.dateStr}`;
                        const info = cellInfo(turniByOssDate[key] || [], ferieByOssDate[key]);
                        const baseBg = d.isSunday ? "bg-rose-50" : d.isSaturday ? "bg-slate-50" : "bg-white";
                        return (
                          <td
                            key={d.dateStr}
                            className={`border border-slate-300 p-0 text-center ${info ? `${info.bg} ${info.text}` : baseBg}`}
                            style={{ width: 44, minWidth: 44, height: 44 }}
                          >
                            <button
                              type="button"
                              onClick={() => openEdit(row, d.dateStr)}
                              aria-label={`${row.cognome || ""} ${row.nome || ""}, ${d.dayNum}: ${info?.label || "nessun turno"} — tocca per modificare`}
                              className="flex h-11 w-11 items-center justify-center touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500"
                            >
                              {info ? (
                                <span className={`font-bold ${info.code.length > 1 ? "text-[10px]" : "text-xs"}`}>{info.code}</span>
                              ) : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <OssTurniLegend className="mt-4" />
        </CardContent>
      </Card>

      <Dialog open={Boolean(editCell)} onOpenChange={(v) => !v && setEditCell(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assegna turno</DialogTitle>
            <DialogDescription>
              {editCell?.nome} ·{" "}
              {editCell?.dateStr
                ? new Date(`${editCell.dateStr}T12:00:00`).toLocaleDateString("it-IT")
                : ""}
              <span className="mt-1 block font-medium text-slate-700" data-testid="oss-turno-attuale">
                Attuale: {cellInfo(editCell?.existing || [], null)?.label || "nessun turno"}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            {TURNI_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant="outline"
                disabled={saving}
                onClick={() => assignTurno(opt.value)}
                className="justify-start min-h-[44px]"
              >
                {opt.label}
              </Button>
            ))}
            {editCell?.existing?.length > 0 && (
              <Button
                variant="destructive"
                disabled={saving}
                onClick={clearTurni}
                className="justify-start"
              >
                <X className="w-4 h-4 mr-2" />
                Rimuovi turni del giorno
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showGenera} onOpenChange={setShowGenera}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Genera turni del mese</DialogTitle>
            <DialogDescription>
              Generazione automatica per copertura. Puoi sostituire i turni esistenti del mese.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Mese</Label>
              <Input
                type="month"
                value={generaMonth}
                onChange={(e) => setGeneraMonth(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="sostituisci"
                checked={generaSostituisci}
                onCheckedChange={(v) => setGeneraSostituisci(Boolean(v))}
              />
              <Label htmlFor="sostituisci" className="font-normal">
                Sostituisci turni già presenti nel mese
              </Label>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Mattina/giorno</Label>
                <Input
                  type="number"
                  min={0}
                  value={generaMattina}
                  onChange={(e) => setGeneraMattina(Number(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1">
                <Label>Pomeriggio/giorno</Label>
                <Input
                  type="number"
                  min={0}
                  value={generaPomeriggio}
                  onChange={(e) => setGeneraPomeriggio(Number(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1">
                <Label>Notte/giorno</Label>
                <Input
                  type="number"
                  min={0}
                  value={generaNotte}
                  onChange={(e) => setGeneraNotte(Number(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>OSS da includere</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {ossStaff.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                    <Checkbox
                      checked={generaOssIds.includes(s.id)}
                      onCheckedChange={() => toggleGeneraOss(s.id)}
                    />
                    {s.cognome} {s.nome}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenera(false)} disabled={generating}>
              Annulla
            </Button>
            <Button type="button" onClick={runGenera} disabled={generating} className="bg-emerald-600 hover:bg-emerald-700">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Genera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
